import {
    InteractionEntity,
    InteractionScoringConfigEntity,
    InteractionScoringEntity,
    InteractionScoringEntityType,
} from '../../../coaching-base/config';
import { InteractionFormValue } from '../../observation-interaction/interaction-form-value.model';
import { ScoreFormValue } from '../../observation-score/score-form-value.model';

import { EvaluationDetails } from './evaluation-details.model';

interface ScoreInput {
    opportunityToDemonstrate: boolean;
    willingnessToTry: boolean;
    proficiencyDelivered: number;
    maxProficiency?: number;
}

interface ScoreOutput {
    opportunityToDemonstrate: number;
    willingnessToTry: number;
    proficiencyDelivered: number;
    maxProficiency?: number;
}

function calcScores(scores: ScoreInput[]): ScoreOutput {
    let otdCount = 0;
    let otd = 0;

    let wttCount = 0;
    let wtt = 0;

    let pdCount = 0;
    let pd = 0;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    scores &&
        scores.forEach((element) => {
            if (element.opportunityToDemonstrate != null) {
                otdCount++;
                if (element.opportunityToDemonstrate) {
                    otd++;
                }
            }

            if (element.willingnessToTry != null) {
                wttCount++;
                if (element.willingnessToTry) {
                    wtt++;
                }
            }

            if (
                element.maxProficiency != null &&
                element.proficiencyDelivered != null
            ) {
                pdCount++;
                pd += element.proficiencyDelivered / element.maxProficiency;
            }
        });

    const result: ScoreOutput = {
        opportunityToDemonstrate: otdCount === 0 ? null : otd / otdCount,
        willingnessToTry: wttCount === 0 ? null : wtt / wttCount,
        proficiencyDelivered: pdCount === 0 ? null : pd / pdCount,
    };

    result.opportunityToDemonstrate = isNaN(result.opportunityToDemonstrate)
        ? null
        : result.opportunityToDemonstrate;
    result.willingnessToTry = isNaN(result.willingnessToTry)
        ? null
        : result.willingnessToTry;
    result.proficiencyDelivered = isNaN(result.proficiencyDelivered)
        ? null
        : result.proficiencyDelivered;

    return result;
}

export interface CalculationResult {
    opportunityToDemonstrate?: number;
    willingnessToTry?: number;
    proficiency?: number;
    opportunityToDemonstrateSession?: number;
    willingnessToTrySession: number;
    proficiencySession: number;
    opportunityToDemonstrateAll?: number;
    willingnessToTryAll: number;
    proficiencyAll: number;
}

export function calculateMain(
    interactionEntity: InteractionEntity,
    evaluationDetails: EvaluationDetails[],
    interactions: InteractionFormValue[],
    published: boolean,
    userModified: boolean,
): CalculationResult {
    const next: CalculationResult = {
        opportunityToDemonstrate: null,
        willingnessToTry: null,
        proficiency: null,
        opportunityToDemonstrateSession: null,
        willingnessToTrySession: null,
        proficiencySession: null,
        opportunityToDemonstrateAll: null,
        willingnessToTryAll: null,
        proficiencyAll: null,
    };

    const inputs1 = (evaluationDetails || []).map((e) => ({
        opportunityToDemonstrate: e.opportunityToDemonstrate,
        willingnessToTry: e.willingnessToTry,
        proficiencyDelivered: e.proficiencyDelivered,
        maxProficiency: e.maxProficiency,
    }));

    const output1 = calcScores(inputs1);

    next.opportunityToDemonstrate = output1.opportunityToDemonstrate;
    next.willingnessToTry = output1.willingnessToTry;
    next.proficiency = output1.proficiencyDelivered;

    /// ///////

    const inputs2 = (interactions || [])
        .filter((e) => e.scoring)
        .reduce<ScoreFormValue[]>((p, c) => {
            p.push(...c.scoring);
            return p;
        }, [])
        .map((e) => ({
            opportunityToDemonstrate: e.opportunityToDemonstrate,
            willingnessToTry: e.willingnessToTry,
            proficiencyDelivered: e.proficiencyDelivered,
            maxProficiency: getScoringConfigMaxProficiency(
                interactionEntity,
                e.scoringConfigurationId,
            ),
        }));

    const output2 = calcScores(inputs2);

    next.opportunityToDemonstrateSession = output2.opportunityToDemonstrate;
    next.willingnessToTrySession = output2.willingnessToTry;
    next.proficiencySession = output2.proficiencyDelivered;

    /// ///////

    // if all interactions have been reset to EMPTY, show no Pending Overalls
    const areAllCurrentScoringEmpty = (interactions || []).every(
        (a) =>
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            a.scoring &&
            a.scoring.every((b) => b.opportunityToDemonstrate == null),
    );

    // Pending overall calculation
    if (!published && userModified && !areAllCurrentScoringEmpty) {
        const inputs3 = [...inputs1, ...inputs2];

        const output3 = calcScores(inputs3);

        next.opportunityToDemonstrateAll = output3.opportunityToDemonstrate;
        next.willingnessToTryAll = output3.willingnessToTry;
        next.proficiencyAll = output3.proficiencyDelivered;
    }

    return next;
}

export function getCurrentScoringConfig(
    entity: InteractionScoringEntity,
): InteractionScoringConfigEntity {
    try {
        return entity.childEntities.find((e) => e.current);
    } catch {
        return null;
    }
}

export function getScoringConfig(
    entity: InteractionScoringEntity,
    scoringConfigurationId: number,
): InteractionScoringConfigEntity {
    try {
        return entity.childEntities.find(
            (e) => e.scoringConfigurationId === scoringConfigurationId,
        );
    } catch {
        return null;
    }
}

function getScoringConfigMaxProficiency(
    entity: InteractionEntity,
    scoringConfigurationId: number,
): number {
    try {
        const scoring = entity.childEntities.find(
            (e) => e.entityType === InteractionScoringEntityType,
        ) as InteractionScoringEntity;
        const scoringConfig = scoring.childEntities.find(
            (e) => e.scoringConfigurationId === scoringConfigurationId,
        );
        return scoringConfig.maxProficiency;
    } catch {
        return null;
    }
}
